import React, { useState } from "react";
import api from "../services/apiService";
import { X } from "lucide-react";

const AddItemModal = ({
  showAddItem,
  setShowAddItem,
  newItem,
  setNewItem,
  allCategories,
  handleAddMenuItem,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  // Handle image file upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = localStorage.getItem("token");
      const res = await api.post("cloudinary/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      let url = null;
      if (res.data) {
        if (typeof res.data === "string" && res.data.startsWith("http")) {
          url = res.data;
        } else if (res.data.url) {
          url = res.data.url;
        } else if (res.data.data && res.data.data.url) {
          url = res.data.data.url;
        }
      }
      if (url) {
        setNewItem({ ...newItem, image: url });
      } else {
        setUploadError(
          "Upload failed: No URL returned. Response: " +
            JSON.stringify(res.data)
        );
        console.log("Image upload response:", res.data);
      }
    } catch (err) {
      setUploadError("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (!showAddItem) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 sm:p-8 w-full max-w-lg sm:max-w-2xl animate-in fade-in duration-200 relative overflow-y-auto max-h-[95vh]">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            Add New Menu Item
          </h3>
          <button
            onClick={() => {
              setShowAddItem(false);
              setNewItem({
                name: "",
                description: "",
                price: "",
                category: "mains",
                image: "",
                quantity: "",
                type: "veg",
              });
            }}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors absolute top-4 right-4"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type *
            </label>
            <select
              value={newItem.type || "veg"}
              onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            >
              <option value="veg">Veg</option>
              <option value="non-veg">Non Veg</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              value={newItem.name}
              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              placeholder="Enter item name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price *
            </label>
            <input
              type="number"
              step="0.01"
              value={newItem.price}
              onChange={(e) =>
                setNewItem({ ...newItem, price: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              value={newItem.category}
              onChange={(e) =>
                setNewItem({ ...newItem, category: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            >
              {allCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            />
            {uploading && (
              <div className="text-xs text-emerald-600 mt-1">Uploading...</div>
            )}
            {uploadError && (
              <div className="text-xs text-red-600 mt-1">{uploadError}</div>
            )}
            <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">
              Or enter Image URL
            </label>
            <input
              type="url"
              value={newItem.image}
              onChange={(e) =>
                setNewItem({ ...newItem, image: e.target.value })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              placeholder="https://example.com/image.jpg"
            />
            {newItem.image && !uploading && (
              <div className="mt-2">
                <img
                  src={newItem.image}
                  alt="Preview"
                  className="h-24 rounded-lg border border-gray-200 object-cover"
                />
                <div className="text-xs text-gray-500 break-all mt-1">
                  {newItem.image}
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity (e.g. 250g, 1kg, 500ml, 1ltr)
            </label>
            <input
              type="text"
              value={newItem.quantity || ""}
              onChange={(e) =>
                setNewItem({ ...newItem, quantity: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              placeholder="e.g. 250g, 1kg, 500ml, 1ltr"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={newItem.description}
              onChange={(e) =>
                setNewItem({ ...newItem, description: e.target.value })
              }
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              placeholder="Enter item description"
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-6 w-full">
          <button
            onClick={handleAddMenuItem}
            className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all"
          >
            Add Item
          </button>
          <button
            onClick={() => {
              setShowAddItem(false);
              setNewItem({
                name: "",
                description: "",
                price: "",
                category: "mains",
                image: "",
                quantity: "",
              });
            }}
            className="px-6 py-3 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddItemModal;
